import { Box, Paper, Tooltip, Typography } from "@mui/material";

// Paleta sobria, distinguishable y accesible: se recorre en orden para que
// dos terapeutas seguidos no compartan color.
const PALETA = [
    "#1F77B4", "#2E9E8F", "#E07A1F", "#8E5FBF", "#C4453C",
    "#4A7DBF", "#6BA833", "#B8860B", "#5C6BC0", "#00897B",
];

const colorDe = (indice) => PALETA[indice % PALETA.length];

// Grafico de barras horizontales con una barra por terapeuta. Cada barra es un
// porcentaje del tope de la escala, que se redondea hacia arriba a un numero
// legible.
//
// datos: [{ etiqueta, valor, descripcion }]
//   etiqueta    texto a la izquierda de la barra
//   valor       numero que define la longitud de la barra
//   descripcion texto del globo; si falta se arma con etiqueta y valor
// formatoValor: como se muestra el valor
const GraficoBarras = ({ datos = [], titulo = "", subtitulo = "", formatoValor }) => {
    if (!datos || datos.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                    No hay horas para graficar en el filtro actual.
                </Typography>
            </Box>
        );
    }

    const texto = (valor) => (formatoValor ? formatoValor(valor) : Number(valor).toFixed(2));

    // Tope de la escala: el maximo redondeado al siguiente orden de magnitud.
    const maximo = Math.max(...datos.map((d) => Number(d.valor) || 0), 0);
    const paso = maximo <= 0 ? 1 : Math.pow(10, Math.floor(Math.log10(maximo)));
    const tope = Math.ceil(maximo / paso) * paso || 1;

    return (
        <Box sx={{ width: "100%" }}>
            {/* Encabezado del grafico */}
            <Box sx={{ paddingBottom: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {titulo}
                </Typography>
                {subtitulo && (
                    <Typography variant="caption" color="text.secondary">
                        {subtitulo}
                    </Typography>
                )}
            </Box>

            <Paper variant="outlined" sx={{ padding: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {datos.map((d, indice) => {
                        const valor = Number(d.valor) || 0;
                        const porcentaje = tope > 0 ? (valor / tope) * 100 : 0;
                        const color = colorDe(indice);
                        const descripcion = d.descripcion
                            || (d.etiqueta + ": " + texto(valor));

                        return (
                            <Box key={d.etiqueta + texto(valor)}
                                sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                {/* Etiqueta del terapeuta */}
                                <Box sx={{ width: 150, flexShrink: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Box sx={{
                                        width: 10, height: 10, borderRadius: "50%",
                                        backgroundColor: color, flexShrink: 0,
                                    }} />
                                    <Typography variant="body2"
                                        title={d.etiqueta}
                                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {d.etiqueta}
                                    </Typography>
                                </Box>

                                {/* Barra */}
                                <Tooltip title={descripcion} placement="top">
                                    <Box sx={{
                                        flexGrow: 1, height: 22,
                                        backgroundColor: "#F1F3F5",
                                        borderRadius: "3px", overflow: "hidden",
                                    }}>
                                        <Box sx={{
                                            width: porcentaje + "%",
                                            height: "100%",
                                            minWidth: valor > 0 ? 3 : 0,
                                            backgroundColor: color,
                                            borderRadius: "3px",
                                            transition: "width .25s ease",
                                        }} />
                                    </Box>
                                </Tooltip>

                                {/* Valor a la derecha */}
                                <Typography variant="body2"
                                    sx={{ width: 68, flexShrink: 0, textAlign: "right", fontWeight: "medium" }}>
                                    {texto(valor)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                <Box sx={{
                    marginTop: 1.5, paddingTop: 1,
                    borderTop: "1px solid #E0E0E0",
                }}>
                    <Typography variant="caption" color="text.secondary">
                        Escala: 0 a {texto(tope)} horas
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}

export default GraficoBarras;
